import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Container, Grid, Typography } from '@mui/material';
import './styles.scss';

const steps = [
  {
    title: 'Secure Allocations',
    body: 'When a contract is signed, funds are programmatically locked as secure Commitments in our sub-ledger. This guarantees that capital is ring-fenced for specific milestones, giving Prime Contractors complete oversight and DBEs total financial security.',
    colorMod: 'action',
  },
  {
    title: 'Digital Lien Waivers',
    body: 'Eliminate the paperwork bottleneck. LusidPay Infra acts as the automated trigger mechanism—the moment an inspector or Prime verifies a project milestone, the digital lien waiver is executed instantly within the platform.',
    colorMod: 'imperial',
  },
  {
    title: 'Frictionless Payouts',
    body: 'No more 60-day waiting periods for vulnerable contractors. Once the digital waiver clears, the secure allocation is immediately routed via our direct-to-supplier payment rails, bridging the gap between physical progress and financial settlement.',
    colorMod: 'bright',
  },
];

const InfrastructurePage = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <Box className="infra-page">

      {/* ── Hero ─────────────────────────────────────────────── */}
      <Box className="infra-hero">
        <Box className="infra-hero__grid-bg" />
        <Container maxWidth="lg">
          <Box className="infra-hero__content">
            <Typography className="infra-hero__overline">
              Institutional-Grade Sub-Ledger
            </Typography>
            <Typography variant="h1" className="infra-hero__title">
              The Financial Ledger for Domestic Infrastructure.
            </Typography>
            <Typography className="infra-hero__subtitle">
              Secure project commitments, automate domestic disbursements, and eliminate the
              payment friction that crushes Disadvantaged Business Enterprises (DBEs) and
              sub-contractors.
            </Typography>
            <Button
              variant="contained"
              className="infra-hero__cta"
              onClick={() => navigate('/contact-us')}
            >
              Speak with Sales
            </Button>
          </Box>
        </Container>
      </Box>

      {/* ── How It Works ─────────────────────────────────────── */}
      <Box className="infra-steps">
        <Container maxWidth="lg">
          <Box className="infra-steps__header">
            <Typography variant="h2" className="infra-steps__title">
              Flawless Execution from Contract to Payout.
            </Typography>
            <Typography className="infra-steps__subtitle">
              We replace slow, manual AP processes with a programmatic ledger designed
              specifically for the physical construction economy.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {steps.map((step) => (
              <Grid item xs={12} md={4} key={step.title}>
                <Box className={`infra-step-card infra-step-card--${step.colorMod}`}>
                  <Typography className="infra-step-card__title">{step.title}</Typography>
                  <Typography className="infra-step-card__body">{step.body}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* ── Enterprise Banner ─────────────────────────────── */}
          <Box className="infra-banner">
            <Box className="infra-banner__content">
              <Typography variant="h3" className="infra-banner__title">
                Powered by Enterprise Treasury Infrastructure.
              </Typography>
              <Typography className="infra-banner__body">
                To execute millions of dollars in infrastructure payouts, you need flawless
                banking rails. Through our deep API partnership with{' '}
                <strong>CurrencyCloud</strong>, LusidPay Infra delivers domestic and
                international payment/collection capabilities directly into your workflow.
              </Typography>
              <ul className="infra-banner__list">
                <li>
                  <span className="infra-banner__check">✓</span>
                  <span>
                    <strong>Domestic Speed:</strong> Rapid ACH and RTP disbursements to
                    local DBEs and sub-contractors.
                  </span>
                </li>
                <li>
                  <span className="infra-banner__check">✓</span>
                  <span>
                    <strong>Global Reach:</strong> Seamless cross-border settlement and FX
                    conversion for Prime Contractors procuring physical construction materials
                    from overseas suppliers.
                  </span>
                </li>
              </ul>
            </Box>
            <Box className="infra-banner__visual">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#60a5fa"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="5" width="20" height="14" rx="2" />
                <line x1="2" y1="10" x2="22" y2="10" />
              </svg>
              <Typography className="infra-banner__visual-title">
                End-to-End Ledgering
              </Typography>
              <Typography className="infra-banner__visual-body">
                Manage multi-tier supplier payments through a single, unified interface.
              </Typography>
            </Box>
          </Box>

        </Container>
      </Box>

    </Box>
  );
};

export default InfrastructurePage;
